import { useLatest } from '@qfa/react-components';
import { FilterInput, SortOrder } from '@kinetic-ui/data-layer';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { localeSort } from 'kineticUtils/data';
import {
  UseFormOptions,
  useDrawer,
  useForm,
  useQueryParams,
  useUserTenantPreference,
} from 'kineticUtils/hooks';
import { intersection, isEmpty, isEqual, uniq } from 'lodash';
import { useHistory } from 'react-router-dom';
import { PREFERENCE_KEYS } from 'types/preferenceTypes';
import { FilterUserPreferences } from './TableFiltersDrawerHeader';
import { UpdateConfig } from './contexts/ColumnsContext/types';
import { BaseRow, SortParams, TABLE_FILTER_PARAMS, TableFilterInput, TableProps } from './types';
import { DateRange, FormValues } from '@qfa/forms';

/** @deprecated - prefer CombinedTableControls */
export const useTableFilterInputContext = <FormType extends FormValues>({
  filterPreferenceKey,
  columnPreferenceKey,
  valueToFilter,
  filtersToValue,
  formOptions,
  defaultValues,
}: {
  filterPreferenceKey?: PREFERENCE_KEYS;
  columnPreferenceKey?: PREFERENCE_KEYS;
  formOptions: UseFormOptions<FormType>;
  valueToFilter: (formValues: Partial<FormType>) => FilterInput[];
  filtersToValue: (filters: FilterInput[]) => Partial<FormType>;
  defaultValues?: Partial<FormType>;
}): TableFilterInput<FormType> => {
  const queryParam = useQueryParams();
  const { replace } = useHistory();
  const queryTableFilters = queryParam.get(TABLE_FILTER_PARAMS);

  const [searchText, setSearchText] = useState<string>('');
  const [defaultPreferenceId, setDefaultPreferenceId] = useState<string>('');
  const [selectedFilterPreference, setSelectedFilterPreference] = useState<string>('');
  const [showFilterChips, setShowFilterChips] = useState<boolean>(true);
  const [columnFiltersOpen, setColumnFiltersOpen] = useState<boolean>(false);
  const [filterDrawerState, filterDrawerControls] = useDrawer();
  const [columnDrawerState, columnDrawerControls] = useDrawer();

  let { setPreference, value: filterUserPreferences = { filters: {}, showFilterChips: true } } =
    useUserTenantPreference<FilterUserPreferences>({ key: filterPreferenceKey! });

  if (filterUserPreferences == null) {
    filterUserPreferences = { filters: {}, showFilterChips: true };
  }

  const formDefaultValues: Partial<FormType> = useMemo(() => {
    let filterValues: Partial<FormType> = {};

    filterUserPreferences?.filters &&
      Object.keys(filterUserPreferences.filters).forEach((key) => {
        if (filterUserPreferences.filters[key].default) {
          filterValues = filtersToValue(filterUserPreferences.filters[key].filters);
        }
      });
    if (isEmpty(filterValues) && defaultValues) {
      return defaultValues;
    }
    return filterValues;
  }, [defaultValues, filterUserPreferences?.filters, filtersToValue]);

  const {
    props: formProps,
    values: [values, setValues, reset],
    isSaveDisabled,
    errors: [hasErrors],
  } = useForm({
    ...formOptions,
    defaultValues: queryTableFilters
      ? filtersToValue(JSON.parse(queryTableFilters))
      : formDefaultValues,
  });

  useEffect(() => {
    const filterInputs = valueToFilter(values);
    filterInputs.sort(localeSort(({ field }) => field));
    let selectedFilterPreference = '';

    if (filterUserPreferences?.filters) {
      Object.keys(filterUserPreferences.filters).forEach((key) => {
        const preferenceFilters = [...filterUserPreferences.filters[key].filters];
        // the order of the filters from values may not match the order in the preference.
        // so we sort them before matching
        preferenceFilters.sort(localeSort(({ field }) => field));
        if (isEqual(filterInputs, preferenceFilters)) {
          selectedFilterPreference = key;
        }
      });
    }

    setSelectedFilterPreference(selectedFilterPreference);
  }, [filterUserPreferences.filters, valueToFilter, values]);

  useEffect(() => {
    // if there are query params, set the active filters to the query params
    if (queryParam.has(TABLE_FILTER_PARAMS)) {
      queryParam.delete(TABLE_FILTER_PARAMS);
      replace({ search: queryParam.toString() });
    }
  }, [queryParam, replace]);

  useEffect(() => {
    if (
      !defaultPreferenceId &&
      (!defaultValues ||
        !valueToFilter(values).length ||
        (defaultValues && valueToFilter(values).length === Object.keys(defaultValues).length)) &&
      filterUserPreferences.filters
    ) {
      Object.keys(filterUserPreferences.filters).forEach((key) => {
        if (filterUserPreferences.filters[key].default) {
          setDefaultPreferenceId(key);
          setSelectedFilterPreference(key);
          setValues(filtersToValue(filterUserPreferences.filters[key].filters));
        }
      });
    }
  }, [
    defaultPreferenceId,
    filterUserPreferences.filters,
    setValues,
    filtersToValue,
    values,
    valueToFilter,
    defaultValues,
  ]);

  const [columnControlsPreference, columnPreferenceChange] = useControlsPreference(
    columnPreferenceKey!
  );

  const setActiveFilterAndSave = useCallback(
    (values: FilterInput[]) => {
      setValues(filtersToValue(values));
    },
    [filtersToValue, setValues]
  );

  const activeFilters = useMemo(() => valueToFilter(values), [valueToFilter, values]);

  const resetForm = useCallback(() => reset({}), [reset]);

  return useMemo(
    () => ({
      activeFilters,
      formProps,
      searchText,
      setSearchText,
      selectedFilterPreference,
      setSelectedFilterPreference,
      showFilterChips,
      setShowFilterChips,
      columnFiltersOpen,
      setColumnFiltersOpen,
      filterPreferenceKey,
      columnPreferenceKey,
      filterDrawerState,
      filterDrawerControls,
      columnDrawerState,
      columnDrawerControls,
      filterUserPreferences,
      saveFilterPreference: setPreference,
      columnControlsPreference,
      columnPreferenceChange,
      setActiveFilters: setActiveFilterAndSave,
      reset: resetForm,
      isSaveDisabled,
      hasErrors,
    }),
    [
      activeFilters,
      formProps,
      setActiveFilterAndSave,
      columnDrawerControls,
      columnDrawerState,
      columnFiltersOpen,
      columnPreferenceKey,
      filterDrawerControls,
      filterDrawerState,
      filterPreferenceKey,
      filterUserPreferences,
      setPreference,
      searchText,
      selectedFilterPreference,
      showFilterChips,
      columnControlsPreference,
      columnPreferenceChange,
      resetForm,
      isSaveDisabled,
      hasErrors,
    ]
  );
};

export const NextSortOrder = {
  [SortOrder.Ascending]: SortOrder.Descending,
  [SortOrder.Descending]: 'NONE',
  NONE: SortOrder.Ascending,
};

export const useTableSelection = ({
  selection,
  setSelection,
  data,
}: Pick<TableProps<BaseRow>, 'selection' | 'setSelection' | 'data'>) => {
  const logSelectionError = (handler?: unknown) => {
    if (!handler) {
      console.error(
        'To use table selection, you need to control the selection state. You need to define `TableProps.selection` and `TableProps.setSelection`'
      );
    }
  };

  const selectRow = useCallback(
    (id: string) => {
      logSelectionError(setSelection);
      setSelection?.((prev) => [...prev, id]);
    },
    [setSelection]
  );

  const selectAll = useCallback(() => {
    logSelectionError(setSelection);
    if (!data) {
      return;
    }
    setSelection?.((prev) => uniq([...prev, ...data.map((row) => row.id)]));
  }, [data, setSelection]);

  const deselectRow = useCallback(
    (id: string) => {
      logSelectionError(setSelection);
      setSelection?.((prev) => prev.filter((prevId) => prevId !== id));
    },
    [setSelection]
  );

  const deselectAll = useCallback(() => {
    logSelectionError(setSelection);
    setSelection?.([]);
  }, [setSelection]);

  const isAllSelected = useMemo(() => {
    const rowsCount = data?.length ?? 0;
    const selectionCount = selection?.length ?? 0;
    const rowIds = data?.map((data) => data.id) ?? [];
    const sameElements = intersection(selection ?? [], rowIds);
    return rowsCount > 0 && selectionCount >= rowsCount && sameElements.length === rowsCount;
  }, [selection, data]);

  return {
    selectRow,
    selectAll,
    deselectRow,
    deselectAll,
    isAllSelected,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useSort = (onSortChange?: TableProps<any>['onSortChange'], sort?: SortParams) => {
  const [sortOrder, setSortOrder] = useState<Record<string, SortOrder>>(
    sort ? { [sort.field]: sort.order } : {}
  );
  const latestOnSortChange = useLatest(onSortChange);
  useEffect(() => {
    onSortChange?.(sortOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOrder]);

  const onSortClick = (name: string) => {
    if (!latestOnSortChange.current) {
      return;
    }
    setSortOrder((prev) => {
      const prevSortOrder = prev?.[name];
      // advance
      if (prevSortOrder) {
        const nextSort = NextSortOrder[prevSortOrder];
        if (nextSort !== 'NONE') {
          return {
            [name]: nextSort as SortOrder,
          };
        }
      }

      // default to ASC
      return {
        [name]: SortOrder.Ascending,
      };
    });
  };

  return [!!onSortChange, sortOrder, onSortClick] as [
    enabled: boolean,
    sortOrder: typeof sortOrder,
    onSortClick: typeof onSortClick
  ];
};

export const useControlsPreference = (key: PREFERENCE_KEYS) => {
  const {
    value: preference,
    loading,
    setPreference,
  } = useUserTenantPreference<{
    columns: UpdateConfig[];
  }>({ key });

  const ready = useRef(false);
  useEffect(() => {
    if (!loading && !ready.current) {
      ready.current = true;
    }
  }, [loading]);

  const onChange = useCallback(
    (columns: UpdateConfig[]) => {
      setPreference({ columns });
    },
    [setPreference]
  );
  return [preference?.columns, onChange, ready.current] as const;
};

export const useActiveFilterCount = (activeFilters = 0, dateRange?: DateRange) => {
  let filtersLength = activeFilters;
  if (dateRange?.startDate || dateRange?.endDate) {
    ++filtersLength;
  }
  return filtersLength;
};
